/* A local, optional enhancement. All historical stages remain readable without it. */
if (!customElements.get('bastet-workshop')) {
  // Paint only while a visitor is doing a step. The original images remain the
  // accessible, printable and no-canvas fallback; no pixels leave this page.
  class BastetScene {
    constructor(scene, index) {
      this.scene = scene;
      this.index = index;
      this.before = scene.querySelector('[data-before]');
      this.after = scene.querySelector('[data-after]');
      this.cleared = scene.querySelector('[data-cleared]');
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'bastet-craft-canvas';
      this.canvas.setAttribute('aria-hidden', 'true');
      // A bounded backing store keeps the activity light on phones.
      this.size = Math.min(1000, Math.max(640, Math.round(scene.clientWidth * Math.min(devicePixelRatio || 1, 2))));
      this.canvas.width = this.canvas.height = this.size;
      this.context = this.canvas.getContext('2d');
      this.layer = document.createElement('canvas');
      this.layer.width = this.layer.height = this.size;
      this.ink = this.layer.getContext('2d');
      this.available = !!(this.context && this.ink);
      if (!this.available) return;
      scene.append(this.canvas);
      this.points = Array.from({ length:index === 0 ? 4200 : 0 }, (_, i) => {
        const rand = salt => { const v = Math.sin((i + 1) * 127.1 + salt * 311.7) * 43758.5453; return v - Math.floor(v); };
        const y = 20 + rand(1) * 945;
        const width = y < 180 ? 88 : y > 860 ? 74 : 101;
        return { x:500 + (rand(2) * 2 - 1) * width, y, order:rand(3), radius:2 + rand(4) * 4 };
      });
    }
    prepare(context) {
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, this.size, this.size);
      context.setTransform(this.size / 1000, 0, 0, this.size / 1000, 0, 0);
    }
    ease(value) { const t = Math.max(0, Math.min(1, value)); return t * t * (3 - 2 * t); }
    clothProgress(progress) { return Math.max(0, (progress - .035) / .965); }
    clothEdge(progress, t) {
      const y = 957 - 970 * this.clothProgress(progress);
      const radius = y < 180 ? 79 : y > 860 ? 70 : 85;
      return { x:500 - Math.cos(t * Math.PI) * radius, y:y + Math.sin(t * Math.PI) * 15 - t * 17 };
    }
    clothPath(context, progress, amount) {
      context.beginPath();
      for (let n = 0; n <= 32; n++) {
        const p = this.clothEdge(progress, amount * n / 32);
        if (n) context.lineTo(p.x, p.y); else context.moveTo(p.x, p.y);
      }
    }
    revealTools(context, progress, y, height) {
      // Fade the whole side prop in with the first action, independently of
      // the material settling on the body. The soft edge blends the paper.
      context.save();context.globalAlpha = this.ease(progress / .025);
      context.shadowColor = '#fff';context.shadowBlur = 15;
      context.fillRect(711, y, 289, height);context.restore();
    }
    band(n, amount = 1) {
      const row = Math.floor(n / 2);
      const flip = n % 2;
      const start = { x:flip ? 591 : 409, y:962 - row * 88 };
      const end = { x:flip ? 409 : 591, y:start.y - 112 };
      return { start, end:{ x:start.x + (end.x - start.x) * amount, y:start.y + (end.y - start.y) * amount } };
    }
    mask(context, progress) {
      context.fillStyle = '#fff';
      context.strokeStyle = '#fff';
      context.lineCap = 'round';
      context.lineJoin = 'round';
      if (this.index === 0) {
        // Seeded grains arrive everywhere, rather than in horizontal sections.
        context.beginPath();
        for (const dot of this.points) {
          const growth = this.ease((progress * 1.3 - dot.order) / .28);
          if (!growth) continue;
          const r = dot.radius * growth * (1 + progress);
          context.moveTo(dot.x + r, dot.y);
          context.arc(dot.x, dot.y, r, 0, Math.PI * 2);
        }
        context.fill();
        this.revealTools(context, progress, 100, 315);
      } else if (this.index === 1) {
        if (this.clothProgress(progress) > 0) {
          this.clothPath(context, progress, 1);
          const left = this.clothEdge(progress, 0), right = this.clothEdge(progress, 1);
          context.lineTo(670,right.y);context.lineTo(670,1000);
          context.lineTo(330,1000);context.lineTo(330,left.y);
          context.closePath();context.fill();
        }
        this.revealTools(context, progress, 370, 280);
      } else if (this.index === 2) {
        // Each diagonal fold finishes the area below it. Keeping the finished
        // lower rows intact avoids exposing isolated patches of the final art.
        for (let i = 0; i < 18; i++) {
          const amount = this.ease(progress * 18 - i);
          if (!amount) continue;
          const { start, end } = this.band(i, amount);
          context.beginPath();context.moveTo(start.x, start.y - 18);
          context.lineTo(end.x, end.y - 18);context.lineTo(end.x, 1000);
          context.lineTo(start.x, 1000);context.closePath();context.fill();
        }
      } else if (this.index === 3) {
        // Three brush passes across the eyes, nose, then muzzle.
        const strokes = [[447, 104, 553, 104, 26], [485, 130, 515, 145, 21], [448, 154, 554, 154, 30]];
        for (let i = 0; i < strokes.length; i++) {
          const t = this.ease(progress * 3 - i);
          if (!t) continue;
          const [x, y, ex, ey, width] = strokes[i];
          context.lineWidth = width;
          context.beginPath();context.moveTo(x, y);context.lineTo(x + (ex - x) * t, y + (ey - y) * t);context.stroke();
        }
      }
    }
    ribbon(context, start, end) {
      context.save();
      const path = () => {
        context.beginPath(); context.moveTo(start.x, start.y);
        context.lineTo(end.x, end.y);
      };
      context.lineCap = 'butt'; context.lineJoin = 'round';
      context.shadowColor = '#68533b55'; context.shadowBlur = 7; context.shadowOffsetY = 5;
      context.strokeStyle = '#b79b70'; context.lineWidth = 35; path(); context.stroke();
      context.shadowColor = 'transparent'; context.strokeStyle = '#ead8b4'; context.lineWidth = 31; path(); context.stroke();
      context.strokeStyle = '#fff5da'; context.lineWidth = 1.2;
      for (const offset of [-10, -4, 4, 10]) { context.save();context.translate(0, offset);path();context.stroke();context.restore(); }
      context.restore();
    }
    atmosphere(context, progress, time) {
      if (this.index === 0) {
        for (let i = 0; i < 85; i++) {
          const dot = this.points[i * 13];
          const phase = (time * 1.15 + dot.order) % 1;
          const fade = Math.sin(phase * Math.PI);
          const x = dot.x + Math.sin(phase * 5 + i) * 8;
          const y = dot.y - (1 - phase) * 210;
          context.globalAlpha = fade * .9;
          context.fillStyle = i % 3 ? '#fffdf0' : '#d5c5a8';
          context.beginPath();context.ellipse(x, y, 1.5 + dot.radius / 5, 2 + dot.radius / 4, phase * 3, 0, Math.PI * 2);context.fill();
        }
        context.globalAlpha = 1;
      } else if (this.index === 1) {
        const cloth = this.clothProgress(progress);
        if (!cloth) return;
        // A short fold follows the body's curved surface. The return passes
        // behind the bundle; there is no suspended strip or floating coil.
        const phase = (cloth * 14) % 1;
        const amount = this.ease(Math.min(1, phase / .7));
        context.save();context.globalAlpha = this.ease(phase / .12) * (1 - this.ease((phase - .75) / .25));
        context.lineCap = 'butt';context.lineJoin = 'round';
        context.shadowColor = '#68533b35';context.shadowBlur = 4;context.shadowOffsetY = 3;
        context.strokeStyle = '#bfa982';context.lineWidth = 25;
        this.clothPath(context, progress, amount);context.stroke();
        context.shadowColor = 'transparent';context.strokeStyle = '#e9d9b8';context.lineWidth = 22;
        this.clothPath(context, progress, amount);context.stroke();
        context.strokeStyle = '#fff5db';context.lineWidth = .8;
        for (const offset of [-7,-3,3,7]) {
          context.save();context.translate(0,offset);this.clothPath(context,progress,amount);context.stroke();context.restore();
        }
        context.restore();
      } else if (this.index === 2) {
        const n = Math.min(17, Math.floor(progress * 18));
        const { start, end } = this.band(n, this.ease(progress * 18 - n));
        this.ribbon(context, start, end);
      } else if (this.index === 3) {
        const pass = Math.min(2, Math.floor(progress * 3));
        const t = this.ease(progress * 3 - pass);
        const positions = [[447,104,553,104], [485,130,515,145], [448,154,554,154]];
        const [x,y,ex,ey] = positions[pass];
        context.save();context.translate(x + (ex - x) * t, y + (ey - y) * t);context.rotate(-.65);
        context.strokeStyle = '#936b3c';context.lineWidth = 7;context.lineCap = 'round';
        context.beginPath();context.moveTo(0,-11);context.lineTo(0,-109);context.stroke();
        context.fillStyle = '#3b3025';context.beginPath();context.moveTo(-5,-18);context.lineTo(5,-18);context.lineTo(0,3);context.closePath();context.fill();
        context.restore();
      }
    }
    offering(context, progress) {
      // Build an empty box from its own wood grain, then move the illustrated
      // bundle separately so its lift, shadow and landing have real depth.
      const landing = this.ease((progress - .18) / .68);
      const lift = Math.sin(landing * Math.PI);
      const slide = 145 * (1 - this.ease(progress / .45));
      context.save();context.translate(slide, 0);
      context.drawImage(this.after, 0, 0, 1000, 1000);
      const w = this.after.naturalWidth, h = this.after.naturalHeight;
      context.drawImage(this.after, w * .8, h * .2, w * .045, h * .58, 329, 48, 213, 864);
      context.restore();
      this.prepare(this.ink);
      this.ink.save();this.ink.beginPath();
      this.ink.moveTo(372, 48);this.ink.quadraticCurveTo(390, 47, 404, 87);
      this.ink.quadraticCurveTo(442, 73, 475, 86);this.ink.lineTo(508, 47);
      this.ink.quadraticCurveTo(524, 54, 516, 103);
      this.ink.bezierCurveTo(542, 151, 503, 189, 484, 201);
      this.ink.bezierCurveTo(477, 224, 525, 233, 522, 354);
      this.ink.lineTo(501, 842);this.ink.quadraticCurveTo(500, 910, 438, 909);
      this.ink.quadraticCurveTo(377, 906, 375, 849);
      this.ink.lineTo(364, 357);this.ink.bezierCurveTo(362, 247, 404, 226, 399, 202);
      this.ink.bezierCurveTo(370, 184, 363, 148, 373, 107);
      this.ink.quadraticCurveTo(364, 72, 372, 48);this.ink.closePath();this.ink.clip();
      this.ink.drawImage(this.after, 0, 0, 1000, 1000);this.ink.restore();
      context.save();
      context.translate(440 + 60 * (1 - landing), 480 - 15 * (1 - landing) - 18 * lift);
      context.rotate(-.045 * lift);context.scale(1 + .09 * (1 - landing) + .03 * lift, 1 + .04 * (1 - landing));
      context.shadowColor = '#3d281a65';context.shadowBlur = 6 + 25 * lift;
      context.shadowOffsetX = 4 + 14 * lift;context.shadowOffsetY = 4 + 24 * lift;
      context.drawImage(this.layer, -440, -480, 1000, 1000);context.restore();
      // Short blends join the moving cutout to the original full illustrations.
      context.save();context.globalAlpha = 1 - this.ease(progress / .14);
      context.drawImage(this.before, 0, 0, 1000, 1000);
      context.globalAlpha = this.ease((progress - .83) / .12);
      context.drawImage(this.after, 0, 0, 1000, 1000);context.restore();
    }
    render(progress, active = false, time = 0) {
      if (!this.available) {
        this.after.style.clipPath = 'none';
        this.after.style.opacity = String(progress);
        return;
      }
      const context = this.context;
      this.prepare(context);
      context.drawImage(this.before, 0, 0, 1000, 1000);
      if (progress >= 1) {
        context.drawImage(this.after, 0, 0, 1000, 1000);
      } else if (progress > 0) {
        if (this.index === 4) {
          this.offering(context, progress);
        } else {
          if (this.index === 1 && this.cleared) {
            context.save();context.globalAlpha = this.ease(progress / .035);
            context.drawImage(this.cleared, 0, 0, 1000, 1000);context.restore();
          }
          // The final few percent also settle the tools/background into place.
          const settle = this.ease((progress - .86) / .14);
          context.save();context.globalAlpha = settle;
          context.drawImage(this.after, 0, 0, 1000, 1000);context.restore();
          this.prepare(this.ink);
          this.mask(this.ink, progress);
          this.ink.globalCompositeOperation = 'source-in';
          this.ink.drawImage(this.after, 0, 0, 1000, 1000);
          this.ink.globalCompositeOperation = 'source-over';
          context.drawImage(this.layer, 0, 0, 1000, 1000);
          if (active) this.atmosphere(context, progress, time);
        }
      }
      this.scene.classList.add('has-craft-canvas');
    }
  }

  customElements.define('bastet-workshop', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.progress = [...this.querySelectorAll('[data-progress-step]')];
      this.status = this.querySelector('[data-status]');
      this.completion = this.querySelector('[data-completion]');
      this.counts = this.steps.map(() => 0);
      this.index = 0;
      this.scenes = [];
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motion.addEventListener('change', () => this.finishAnimation?.(), { signal:this.controller.signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.finishAnimation?.(); }, { signal:this.controller.signal });
      this.classList.add('is-enhanced');
      this.querySelectorAll('[data-controls], [data-progress]').forEach(el => { el.hidden = false; });
      this.steps.forEach(step => {
        step.querySelector('[data-before]').hidden = false;
        const scene = step.querySelector('.bastet-game-image');
        scene.setAttribute('role', 'img');
        scene.setAttribute('aria-label', step.dataset.beforeAlt);
        scene.querySelectorAll('img').forEach(img => img.setAttribute('aria-hidden', 'true'));
      });
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-reset')) return this.reset();
        if (button.hasAttribute('data-next')) return this.next();
        if (button.hasAttribute('data-back')) return this.show(this.index - 1, true);
        if (button.hasAttribute('data-tool')) this.useTool(button);
      }, { signal:this.controller.signal });
      this.show(0);
    }
    disconnectedCallback() {
      this.revision = (this.revision || 0) + 1;
      this.cancelAnimation?.();
      this.controller?.abort();
      this.controller = null;
    }
    show(index, focus = false) {
      this.cancelAnimation?.();
      this.revision = (this.revision || 0) + 1;
      this.busy = false;
      this.index = Math.max(0, Math.min(index, this.steps.length - 1));
      this.completion.hidden = true;
      this.steps.forEach((step, i) => { step.hidden = i !== this.index; });
      this.progress.forEach((item, i) => {
        item.classList.toggle('is-done', this.isComplete(i));
        if (i === this.index) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
      const current = this.steps[this.index];
      // A fully clipped lazy image may never intersect the viewport. Load only
      // the active pair explicitly so decode() can finish before the reveal.
      current.querySelectorAll('img').forEach(img => { img.loading = 'eager'; });
      current.querySelector('[data-back]').hidden = this.index === 0;
      this.restoreScene(this.index);
      this.status.textContent = `Step ${this.index + 1} of ${this.steps.length}. Take your time; there is no timer.`;
      if (focus) current.querySelector('h3').focus({ preventScroll:true });
    }
    isComplete(index) {
      return this.counts[index] >= Number(this.steps[index].dataset.actions);
    }
    lesson(step, count) {
      return count ? step.dataset[`lesson-${count}`] || step.dataset.finished : step.dataset.startHint;
    }
    async useTool(button) {
      const index = this.index;
      const revision = this.revision;
      const step = this.steps[index];
      if (this.isComplete(index) || this.busy) return;
      const feedback = step.querySelector('[data-feedback]');
      if (button.dataset.correct !== 'true') {
        feedback.textContent = button.dataset.hint;
        return;
      }
      const after = step.querySelector('[data-after]');
      const scene = step.querySelector('.bastet-game-image');
      const needed = Number(step.dataset.actions);
      const count = this.counts[index] + 1;
      this.busy = true;
      scene.setAttribute('aria-busy', 'true');
      step.querySelectorAll('[data-tool]').forEach(tool => tool.setAttribute('aria-disabled', 'true'));
      let renderer;
      try {
        feedback.textContent = this.lesson(step, count);
        await Promise.all([step.querySelector('[data-before]'), after, step.querySelector('[data-cleared]')].filter(Boolean).map(img => img.decode()));
        if (revision !== this.revision) return;
        renderer = this.scenes[index] ||= new BastetScene(scene, index);
      } catch {
        if (revision !== this.revision) return;
        feedback.textContent = 'The illustration could not load. Try again when your connection is ready.';
        this.releaseStep(step);
        return;
      }
      if (revision !== this.revision) return;
      button.setAttribute('aria-pressed', 'true');
      const finished = await this.animateScene(renderer, this.counts[index] / needed, count / needed, index);
      if (!finished || revision !== this.revision) return;
      this.counts[index] = count;
      this.releaseStep(step);
      scene.style.setProperty('--reveal', `${100 * count / needed}%`);
      step.querySelector('.bastet-game-image').setAttribute('aria-label', count === needed ? after.alt : `${step.dataset.actionLabel}, ${count} of ${needed}, being added to the illustration.`);
      feedback.textContent = this.lesson(step, count);
      if (count === needed) {
        step.querySelector('[data-next]').hidden = index === this.steps.length - 1;
        step.querySelectorAll('[data-tool]').forEach(tool => { tool.setAttribute('aria-disabled', 'true'); });
        this.progress[index].classList.add('is-done');
        this.status.textContent = `Step ${index + 1} complete. Continue when you are ready.`;
        if (index === this.steps.length - 1) this.next();
      }
    }
    releaseStep(step) {
      this.busy = false;
      step.querySelector('.bastet-game-image').removeAttribute('aria-busy');
      step.querySelectorAll('[data-tool]').forEach(tool => tool.removeAttribute('aria-disabled'));
    }
    async restoreScene(index) {
      const revision = this.revision;
      const step = this.steps[index];
      this.releaseStep(step);
      if (this.isComplete(index)) step.querySelectorAll('[data-tool]').forEach(tool => tool.setAttribute('aria-disabled', 'true'));
      step.querySelector('[data-feedback]').textContent = this.lesson(step, this.counts[index]);
      try {
        await Promise.all([...step.querySelectorAll('img')].map(img => img.decode()));
        if (revision !== this.revision || this.busy) return;
        const renderer = this.scenes[index] ||= new BastetScene(step.querySelector('.bastet-game-image'), index);
        renderer.render(this.counts[index] / Number(step.dataset.actions));
      } catch { /* The static image and retryable controls remain available. */ }
    }
    animateScene(renderer, from, to, index) {
      if (this.motion.matches || document.hidden) { renderer.render(to); return Promise.resolve(true); }
      return new Promise(resolve => {
        const duration = [2300, 3300, 3600, 1750, 3600][index];
        let start;
        let last = -Infinity;
        let frame;
        const finish = completed => {
          cancelAnimationFrame(frame);
          this.cancelAnimation = null;
          this.finishAnimation = null;
          renderer.render(completed ? to : from);
          resolve(completed);
        };
        this.cancelAnimation = () => finish(false);
        this.finishAnimation = () => finish(true);
        const tick = now => {
          start ??= now;
          const t = Math.min(1, (now - start) / duration);
          if (t >= 1) return finish(true);
          // Cap painting at 30fps. The browser can stay responsive to scrolling.
          if (now - last >= 30) {
            last = now;
            const eased = t * t * (3 - 2 * t);
            renderer.render(from + (to - from) * eased, true, (now - start) / 1000);
          }
          frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      });
    }
    next() {
      if (!this.isComplete(this.index)) return;
      if (this.index < this.steps.length - 1) return this.show(this.index + 1, true);
      this.steps.forEach(step => { step.hidden = true; });
      this.progress.forEach(item => item.removeAttribute('aria-current'));
      this.completion.hidden = false;
      this.status.textContent = 'All five stages complete.';
      this.completion.querySelector('h3').focus({ preventScroll:true });
    }
    reset() {
      this.cancelAnimation?.();
      this.counts.fill(0);
      this.steps.forEach(step => {
        step.querySelector('.bastet-game-image').style.setProperty('--reveal', '0%');
        step.querySelector('[data-after]').style.removeProperty('clip-path');
        step.querySelector('.bastet-game-image').setAttribute('aria-label', step.dataset.beforeAlt);
        step.querySelector('[data-feedback]').textContent = step.dataset.startHint;
        step.querySelector('[data-next]').hidden = true;
        step.querySelectorAll('[data-tool]').forEach(tool => {
          tool.setAttribute('aria-pressed', 'false');
          tool.removeAttribute('aria-disabled');
        });
      });
      this.show(0, true);
    }
  });
}
